import { useState } from "react";
import { Button, Stepper } from "../ui";
import { cellSize, clampGrid, emptyCellRects, gridLines, trailingEmptyCells } from "./gridPlan";

// §9.17/D139 — Manuelle Grid-Eingabe (Stufe C: detect_grid scheiterte) bzw. Korrektur über das
// Grid-Badge. cols/rows/frameCount-Stepper. D139-Amendment (User): das Original-Sheet wird mit
// einem LIVE-Raster-Overlay angezeigt — WYSIWYG, ob das eingegebene cols×rows-Gitter zur Sheet-
// Geometrie passt; leere End-Zellen werden abgedunkelt. Die Zell-Geometrie ist PURE in gridPlan
// getestet (gridLines/emptyCellRects). Ohne Sheet-Bild fällt der Dialog auf die rein numerische
// Eingabe zurück. Abbruch behält das Bild als Einzelframe (Aufrufer-Sache).

export function GridDialog({
  initial,
  sheet,
  onApply,
  onCancel,
}: {
  initial: { cols: number; rows: number; frameCount: number };
  sheet?: { url: string; width: number; height: number } | null;
  onApply: (grid: { cols: number; rows: number; frameCount: number }) => void;
  onCancel: () => void;
}) {
  // Codex-P2-1: Initial-State bereits geklemmt (≥1 UND ≤64, frameCount ≤ cols×rows) — sonst kann ein
  // ungültiges Badge-/Deep-Link-Grid roh in den Stepper-State laufen.
  const init = clampGrid(initial.cols, initial.rows, initial.frameCount);
  const [cols, setCols] = useState(init.cols);
  const [rows, setRows] = useState(init.rows);
  const [frameCount, setFrameCount] = useState(init.frameCount);
  const spec = clampGrid(cols, rows, frameCount);
  const empty = trailingEmptyCells(spec);

  // Overlay-Geometrie in Sheet-Pixeln (das SVG streckt sie via viewBox auf die <img>-Box). Der
  // entpackte Bereich ist cols×cellW / rows×cellH (floor — Spiegel spritesheet_unpack); ein nicht
  // ganzzahlig teilbares Sheet hat rechts/unten einen Reststreifen, der VERWORFEN wird (Codex-P2-2:
  // ehrlich auszeichnen statt den Rahmen über die volle Sheet-Breite zu legen).
  const lines = sheet ? gridLines(sheet.width, sheet.height, spec.cols, spec.rows) : null;
  const empties = sheet ? emptyCellRects(spec, sheet.width, sheet.height) : [];
  const cell = sheet ? cellSize(sheet.width, sheet.height, spec.cols, spec.rows) : { cellW: 0, cellH: 0 };
  const gridW = cell.cellW * spec.cols;
  const gridH = cell.cellH * spec.rows;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl2 border border-border bg-surface p-5 shadow-xl">
        <h3 className="mb-1 text-base font-semibold text-text">Spritesheet-Gitter angeben</h3>
        <p className="mb-4 text-xs text-muted">
          Gib Spalten, Zeilen und die Anzahl belegter Frames an
          {sheet ? " — die Vorschau zeigt das Raster über dem Spritesheet" : ""}.
        </p>

        {sheet && lines && (
          <div className="mb-4 flex justify-center">
            <div className="relative inline-block max-w-full overflow-hidden rounded-lg border border-border bg-surface-2">
              <img
                src={sheet.url}
                alt="Spritesheet"
                draggable={false}
                className="block max-h-72 max-w-full select-none"
              />
              {/* preserveAspectRatio="none" + viewBox = Sheet-Maße ⇒ Linien sind über dem
                  inline-block-gewrappten <img> deckungsgleich; non-scaling-stroke hält 1 px. */}
              <svg
                className="pointer-events-none absolute inset-0 h-full w-full"
                viewBox={`0 0 ${sheet.width} ${sheet.height}`}
                preserveAspectRatio="none"
                aria-hidden
              >
                {empties.map((r, i) => (
                  <rect key={`e${i}`} x={r.x} y={r.y} width={r.w} height={r.h} fill="rgba(2,6,23,0.55)" />
                ))}
                {/* Codex-P2-2: verworfener Reststreifen rechts/unten (nicht ganzzahlig teilbar). Der
                    rechte Streifen läuft über die volle Höhe; der untere endet bei gridW ⇒ die untere
                    rechte Ecke wird NUR vom rechten Streifen gedeckt (keine Doppel-Abdunklung). */}
                {gridW < sheet.width && (
                  <rect x={gridW} y={0} width={sheet.width - gridW} height={sheet.height} fill="rgba(2,6,23,0.55)" />
                )}
                {gridH < sheet.height && (
                  <rect x={0} y={gridH} width={gridW} height={sheet.height - gridH} fill="rgba(2,6,23,0.55)" />
                )}
                <rect
                  x={0}
                  y={0}
                  width={gridW}
                  height={gridH}
                  fill="none"
                  stroke="rgba(129,140,248,0.95)"
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                />
                {lines.vertical.map((x, i) => (
                  <line
                    key={`v${i}`}
                    x1={x}
                    y1={0}
                    x2={x}
                    y2={gridH}
                    stroke="rgba(129,140,248,0.75)"
                    strokeWidth={1}
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
                {lines.horizontal.map((y, i) => (
                  <line
                    key={`h${i}`}
                    x1={0}
                    y1={y}
                    x2={gridW}
                    y2={y}
                    stroke="rgba(129,140,248,0.75)"
                    strokeWidth={1}
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
              </svg>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <label className="flex items-center justify-between text-sm text-text">
            Spalten
            <Stepper value={cols} min={1} max={64} onChange={setCols} />
          </label>
          <label className="flex items-center justify-between text-sm text-text">
            Zeilen
            <Stepper value={rows} min={1} max={64} onChange={setRows} />
          </label>
          <label className="flex items-center justify-between text-sm text-text">
            Frames
            {/* Codex-P2-1: GEKLEMMTEN Wert anzeigen — sinkt cols/rows, würde der rohe State sonst
                kurzzeitig > cols×rows zeigen, während Summary/Overlay/Apply bereits spec nutzen. */}
            <Stepper value={spec.frameCount} min={1} max={spec.cols * spec.rows} onChange={setFrameCount} />
          </label>
          <p className="text-xs text-muted">
            {spec.cols}×{spec.rows} = {spec.cols * spec.rows} Zellen, {spec.frameCount} Frames
            {empty > 0 ? ` (${empty} leer — abgedunkelt)` : ""}.
          </p>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>
            Als Einzelbild
          </Button>
          <Button onClick={() => onApply(spec)}>Übernehmen</Button>
        </div>
      </div>
    </div>
  );
}
